import { SearchOutlined } from '@ant-design/icons';
import type { InputRef } from 'antd';
import { Button, Input, Space } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import type { FilterDropdownProps, TableLocale } from 'antd/es/table/interface';
import type { ChangeEvent, FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ColumnType } from '../index';

interface OwnProps<RecordType = AnyObject> extends Omit<FilterDropdownProps, 'filters'> {
  column: ColumnType<RecordType>;
  dropdownPrefixCls: string;
  locale: TableLocale;
}

const FilterInputDropdown: FC<OwnProps> = ({
  column,
  prefixCls,
  dropdownPrefixCls,
  setSelectedKeys,
  selectedKeys,
  confirm,
  close,
  clearFilters,
  visible,
  locale,
}) => {
  const searchInput = useRef<InputRef>(null);

  useEffect(() => {
    if (visible) {
      setTimeout(() => searchInput?.current?.select(), 200);
    }
  }, [visible]);

  const { filterResetToDefaultFilteredValue, defaultFilteredValue } = column;

  const [searchValue, setSearchValue] = useState<string>();
  const filteredValue = selectedKeys.length > 0 ? String(selectedKeys[0]) : undefined;

  const getResetDisabled = () => {
    if (filterResetToDefaultFilteredValue) {
      return (defaultFilteredValue || []).map((key) => String(key))[0] === filteredValue;
    }

    return filteredValue === undefined;
  };

  const getConfirmDisabled = () => {
    return searchValue === undefined || searchValue.replace(/^\s+/, '') === '';
  };

  const getConfirmEmptyDisabled = () => {
    return filteredValue === '';
  };

  const onConfirm = () => {
    if (searchValue === undefined || getConfirmDisabled()) {
      return;
    }
    setSelectedKeys([searchValue.replace(/^\s+/, '')]);
    confirm();
  };

  const onConfirmEmpty = () => {
    if (getConfirmEmptyDisabled()) {
      return;
    }
    setSelectedKeys(['']);
    confirm();
  };

  const onReset: typeof clearFilters = (params) => {
    if (getResetDisabled()) {
      return;
    }

    clearFilters?.(params);
    confirm();
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchValue(value);
  };

  const getFilterComponent = () => {
    const placeholder = `Search by ${column.title || column.key || String(column.dataIndex)}`;
    return (
      <div className={`${dropdownPrefixCls}-input`} style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          prefix={<SearchOutlined />}
          placeholder={placeholder}
          // for skip min-width of input
          htmlSize={1}
          className={`${prefixCls}-dropdown-search-input`}
          defaultValue={filteredValue}
          value={searchValue}
          onChange={onChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onConfirm();
              return;
            } else if (event.key === 'Escape') {
              close();
              return;
            }
          }}
        />
      </div>
    );
  };

  return (
    <>
      {getFilterComponent()}
      <div className={`${prefixCls}-dropdown-btns`}>
        <Button type="link" size="small" disabled={getResetDisabled()} onClick={() => onReset()}>
          {locale?.filterReset}
        </Button>
        <Space.Compact block style={{ flexBasis: 'content' }}>
          <Button
            color="default"
            variant="filled"
            size="small"
            disabled={getConfirmEmptyDisabled()}
            onClick={onConfirmEmpty}
          >
            Filter Empty
          </Button>
          <Button type="primary" size="small" disabled={getConfirmDisabled()} onClick={onConfirm}>
            {locale?.filterConfirm}
          </Button>
        </Space.Compact>
      </div>
    </>
  );
};

export default FilterInputDropdown;
